import React, { forwardRef, useState } from 'react';
import './Input.css'; // Nous créerons ce fichier CSS séparément

const Input = forwardRef(({
  className,
  type = "text",
  label,
  description,
  error,
  required = false,
  id,
  style,
  labelStyle,
  inputStyle,
  descriptionStyle,
  errorStyle,
  onFocus,
  onBlur,
  ...props
}, ref) => {
  // Generate unique ID if not provided
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  // Handle focus state
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = (e) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  // Checkbox-specific render
  if (type === "checkbox") {
    return (
      <div 
        className={`input-container ${className || ''}`}
        style={style}
      >
        <label 
          className={`checkbox-label ${props.disabled ? 'disabled' : ''}`}
          style={{ cursor: props.disabled ? 'not-allowed' : 'pointer' }}
        >
          <input
            ref={ref}
            type="checkbox"
            id={inputId}
            className="checkbox-input"
            checked={props.value || false}
            onChange={(e) => props.onChange?.(e.target.checked)}
            disabled={props.disabled}
            style={{ display: 'none' }}
          />
          <span className={`checkbox-custom ${props.value ? 'checked' : ''} ${props.disabled ? 'disabled' : ''}`}>
            {props.value && <span className="checkbox-inner" />}
          </span>
          {label && (
            <span className="checkbox-text" style={labelStyle}>
              {label}
              {required && <span className="required">*</span>}
            </span>
          )}
        </label>
        {description && !error && (
          <div className="description" style={descriptionStyle}>
            {description}
          </div>
        )}
        {error && (
          <div className="error" style={errorStyle}>
            {error}
          </div>
        )}
      </div>
    );
  }

  // Radio button-specific render
  if (type === "radio") {
    return (
      <div 
        className={`input-container ${className || ''}`}
        style={style}
      >
        <label 
          className={`radio-label ${props.disabled ? 'disabled' : ''}`}
          style={{ cursor: props.disabled ? 'not-allowed' : 'pointer' }}
        >
          <input
            ref={ref}
            type="radio"
            id={inputId}
            className="radio-input"
            name={props.name}
            checked={props.checked || false}
            onChange={(e) => props.onChange?.(props.value)}
            disabled={props.disabled}
            style={{ display: 'none' }}
          />
          <span className={`radio-custom ${props.checked ? 'checked' : ''} ${props.disabled ? 'disabled' : ''}`}>
            {props.checked && <span className="radio-inner" />}
          </span>
          {label && (
            <span className="radio-text" style={labelStyle}>
              {label}
              {required && <span className="required">*</span>}
            </span>
          )}
        </label>
        {description && !error && (
          <div className="description" style={descriptionStyle}>
            {description}
          </div>
        )}
        {error && (
          <div className="error" style={errorStyle}>
            {error}
          </div>
        )}
      </div>
    );
  }

  // Determine input type for web
  const getInputType = () => {
    switch (type) {
      case 'textarea':
        return 'textarea';
      case 'email':
        return 'email';
      case 'tel':
      case 'phone':
        return 'tel';
      case 'number':
        return 'number';
      case 'url':
        return 'url';
      case 'password':
        return 'password';
      default:
        return 'text';
    }
  };

  // For regular inputs
  const inputType = getInputType();
  const isTextarea = inputType === 'textarea';

  return (
    <div 
      className={`input-container ${className || ''}`}
      style={style}
    >
      {label && (
        <label 
          htmlFor={inputId}
          className={`label ${error ? 'label-error' : 'label-normal'}`}
          style={labelStyle}
        >
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}

      <div 
        className={`input-wrapper ${isFocused ? 'focused' : ''} ${error ? 'error' : ''} ${props.disabled ? 'disabled' : ''}`}
      >
        {isTextarea ? (
          <textarea
            ref={ref}
            id={inputId}
            className={`input ${props.multiline ? 'multiline' : ''}`}
            style={inputStyle}
            placeholder={props.placeholder}
            value={props.value || ''}
            onChange={props.onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={props.disabled}
            rows={props.multiline ? props.numberOfLines || 4 : 4}
            maxLength={props.maxLength}
            {...props}
          />
        ) : (
          <input
            ref={ref}
            id={inputId}
            type={inputType}
            className="input"
            style={inputStyle}
            placeholder={props.placeholder}
            value={props.value || ''}
            onChange={props.onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={props.disabled}
            maxLength={props.maxLength}
            {...props}
          />
        )}
      </div>

      {description && !error && (
        <div className="description" style={descriptionStyle}>
          {description}
        </div>
      )}

      {error && (
        <div className="error" style={errorStyle}>
          {error}
        </div>
      )}
    </div>
  );
});

Input.displayName = "Input";

export default Input;